/* eslint-disable @typescript-eslint/require-await */
import { Injectable } from '@angular/core';

export type FileRecord = {
    id?: number;
    metadata: FileMetadata;
    timestamp: number;
    status: 'pending' | 'uploading' | 'completed' | 'failed' | 'duplicate';
    retryCount: number;
    error?: string;
    userId?: string;
    tenantId?: string;
    locationId?: string; // Location where file was transferred - preserved for upload
};

export type FileMetadata = {
    locationId?: string;
    filename: string;
    device: string;
    transferTimestamp: number;
    fileSize: number;
    fileNumber: number;
};

export type UploadResult = {
    success: boolean;
    duplicate?: boolean;
    error?: string;
};

@Injectable({
    providedIn: 'root'
})
export class YmodemStorageService {
    private readonly storageKey = 'ymodem_file_records';
    private records: FileRecord[] = [];
    private nextId = 1;

    public constructor() {
        this.loadFromStorage();
    }

    private loadFromStorage(): void {
        try {
            const stored = localStorage.getItem(this.storageKey);
            this.records = stored ? (JSON.parse(stored) as FileRecord[]) : [];
        } catch {
            this.records = [];
        }
        this.nextId = this.records.length > 0 ? Math.max(...this.records.map((r) => r.id ?? 0)) + 1 : 1;
    }

    private persist(): void {
        localStorage.setItem(this.storageKey, JSON.stringify(this.records));
    }

    public async storeTransferredFile(metadata: FileMetadata, userId?: string, tenantId?: string): Promise<number> {
        const id = this.nextId++;
        this.records.push({
            id,
            metadata,
            timestamp: Date.now(),
            status: 'pending',
            retryCount: 0,
            userId,
            tenantId,
            locationId: metadata.locationId // Store location separately for easy access during uploads
        });
        this.persist();
        return id;
    }

    public async getPendingUploads(): Promise<FileRecord[]> {
        return [...this.records];
    }

    public async getPendingUploadsByUserTenant(
        userId: string | undefined,
        tenantId: string | undefined
    ): Promise<FileRecord[]> {
        if (!userId || !tenantId) {
            //console.warn('User context missing - cannot filter files by user/tenant');
            return [];
        }
        return this.records.filter((r) => r.userId === userId && r.tenantId === tenantId);
    }

    public async updateUploadStatus(id: number, status: FileRecord['status'], error?: string): Promise<void> {
        const record = this.records.find((r) => r.id === id);
        if (record) {
            record.status = status;
            record.error = error;
            if (status === 'pending') {
                record.retryCount++;
            }
            this.persist();
        }
    }

    public async removeUploadedFile(id: number): Promise<void> {
        this.records = this.records.filter((r) => r.id !== id);
        this.persist();
    }

    public async clearAllRecords(): Promise<void> {
        this.records = [];
        localStorage.removeItem(this.storageKey);
    }
}
